import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { format, isSameMonth, isSameYear, isSameWeek, getWeekOfMonth, parseISO } from 'date-fns';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, LineController, Legend, } from 'chart.js';
import PieChartLegend from '../SpendReport/PieChart/PieChartLegend';
import DateRangePicker from '../DateRangePicker';
ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, PointElement, LineElement, LineController);
const legends = [
    {
        label: 'Impressions',
        color: '#6E60C4',
    },
    {
        label: 'Spend',
        color: '#9F92ED',
    },
];
function CampaignAnalyticsChart({ data, onSubmit, defaultDuration }) {
    const impressionsData = data.map((item) => item.impressions);
    const spendData = data.map((item) => item.spend);
    const [duration, setDuration] = useState({
        from: defaultDuration.from,
        to: defaultDuration.to,
    });
    const setDates = (dates) => {
        setDuration(() => dates);
        onSubmit(dates);
    };
    data.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
    let ranges = [
        { divider: 1e6, suffix: 'M' },
        { divider: 1e3, suffix: 'k' },
    ];
    function formatNumber(n) {
        for (var i = 0; i < ranges.length; i++) {
            if (n >= ranges[i].divider) {
                return Math.round(n / ranges[i].divider).toString() + ranges[i].suffix;
            }
        }
        return n;
    }
    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                padding: 16,
                boxWidth: 8,
                boxHeight: 8,
                boxPadding: 5,
                backgroundColor: '#172B4D',
                footerFont: {
                    size: 12,
                    weight: '400',
                    lineHeight: 1.5,
                },
                callbacks: {
                    label: function (context) {
                        return '';
                    },
                    beforeFooter: function (context) {
                        let spend = 'Spend: ' + formatNumber(spendData[context[0].dataIndex]);
                        return spend;
                    },
                    afterFooter: function (context) {
                        let impression = 'Impressions:  ' +
                            formatNumber(impressionsData[context[0].dataIndex]);
                        return impression;
                    },
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: {
                ticks: {
                    maxTicksLimit: 5,
                },
            },
            spend: {
                type: 'linear',
                display: true,
                position: 'right',
                ticks: {
                    callback(tickValue, index, ticks) {
                        return formatNumber(tickValue);
                    },
                },
                title: {
                    display: true,
                    text: 'Spend',
                },
            },
            impression: {
                type: 'linear',
                display: true,
                position: 'left',
                grid: {
                    display: false,
                },
                ticks: {
                    callback(tickValue) {
                        return formatNumber(tickValue);
                    },
                },
                title: {
                    display: true,
                    text: 'Impression',
                },
            },
        },
    };
    const sameMonth = data.every((item) => isSameMonth(parseISO(data[0].date), parseISO(item.date)));
    const sameYear = data.every((item) => isSameYear(parseISO(data[0].date), parseISO(item.date)));
    const sameWeek = data.every((item) => isSameWeek(parseISO(data[0].date), parseISO(item.date)));
    const labels = data.map((i) => {
        let labelFormat;
        if (sameYear && !sameMonth) {
            labelFormat = format(new Date(i.date), 'd MMM y');
        }
        else if (sameMonth && sameYear && !sameWeek) {
            labelFormat =
                format(new Date(i.date), 'MMM') +
                    ', Week' +
                    getWeekOfMonth(new Date(i.date));
        }
        else if (sameWeek) {
            labelFormat = format(new Date(i.date), 'd MMM y');
        }
        else {
            labelFormat = format(new Date(i.date), 'MMM y');
        }
        return labelFormat;
    });
    const chartData = {
        labels,
        datasets: [
            {
                data: data.map((i) => i.spend),
                type: 'line',
                backgroundColor: '#9F92ED',
                borderColor: '#9F92ED',
                borderWidth: 2,
                yAxisID: 'spend',
                hoverRadius: 4,
                hoverBorderWidth: 4,
                pointRadius: 4,
                tension: 0.5,
            },
            {
                type: 'line',
                data: data.map((i) => i.impressions),
                backgroundColor: '#6E60C4',
                borderColor: '#6E60C4',
                borderWidth: 2,
                yAxisID: 'impression',
                pointStyle: 'rectRot',
                hoverRadius: 4,
                hoverBorderWidth: 4,
                pointRadius: 4,
                tension: 0.5,
            },
        ],
    };
    return (_jsxs(Box, Object.assign({ sx: { width: '100%' } }, { children: [_jsxs(Box, Object.assign({ display: "flex", alignItems: "center", justifyContent: "space-between" }, { children: [_jsx(Typography, Object.assign({ variant: "h4", fontWeight: "SemiBold", sx: { margin: '16px 0px' } }, { children: "Campaign Summary" })), _jsx(Box, Object.assign({ display: "flex", alignItems: "center" }, { children: legends.map((legend) => (_jsx(Box, Object.assign({ color: legend.color, mr: '16px' }, { children: _jsx(PieChartLegend, { label: legend.label, legendColor: legend.color }) }), legend.label))) })), _jsx(DateRangePicker, { duration: duration, setDuration: setDates })] })), _jsx(Box, Object.assign({ sx: {
                    height: '350px',
                } }, { children: _jsx(Chart, { type: "line", options: options, data: chartData }) }))] })));
}
export default CampaignAnalyticsChart;
